import { botPath, getData } from './restApi';

// Ola 2: 'true' reads contacts (list, lookup, detail, tags, chat sidebar) from main_service REST.
// Writes stay on GraphQL.
export const isContactsRest = (): boolean => process.env.NEXT_PUBLIC_REST_CONTACTS === 'true';

type Row = Record<string, any>;

export type ContactListFilters = {
  scope?: 'bot' | 'org';
  tags?: string[];
  keyword?: string;
  unassigned?: boolean;
  conversation?: 'with' | 'without' | 'all';
  leadStatusIds?: string[];
  assignmentUserIds?: string[];
  countries?: string[];
  assignedToMe?: boolean;
  limit: number;
  offset: number;
  includeCount?: boolean;
};

const realIds = (ids?: string[]) => (ids?.length && !ids.includes('all') ? ids : []);

// Same filters as buildQueryName + buildHasuraQuery; sales are scoped to their own sessions server side.
export const buildContactListParams = (f: ContactListFilters): URLSearchParams => {
  const params = new URLSearchParams({ limit: String(f.limit), offset: String(f.offset) });
  if (f.includeCount) params.set('include_count', 'true');
  if (f.scope === 'org') params.set('scope', 'org');
  (f.tags ?? []).forEach((tag) => (
    tag === 'No tags' ? params.set('no_tags', 'true') : params.append('tags', tag)
  ));
  if (f.keyword) params.set('q', f.keyword);
  if (f.unassigned || f.assignmentUserIds?.includes('unassigned')) params.set('unassigned', 'true');
  else realIds(f.assignmentUserIds).forEach((id) => params.append('assignment_user_ids', id));
  if (f.conversation === 'with' || f.conversation === 'without') {
    params.set('has_conversation', String(f.conversation === 'with'));
  }
  realIds(f.leadStatusIds).forEach((id) => params.append('lead_status_ids', id));
  (f.countries ?? []).forEach((c) => params.append('countries', c));
  if (f.assignedToMe) params.set('assigned_to_me', 'true');
  return params;
};

// Rows come back with `bot_sessions`, as the GetContacts rows the screens already map.
export async function fetchContactsRest(
  botId: string,
  f: ContactListFilters,
): Promise<{ contacts: Row[]; total?: number }> {
  const data = await getData(botPath(botId, '/contacts'), buildContactListParams(f));
  const contacts = data.items.map((c: Row) => ({ ...c, bot_sessions: c.sessions }));
  return { contacts, total: data.total ?? undefined };
}

export const fetchContactRest = (botId: string, contactId: string): Promise<Row> => (
  getData(botPath(botId, `/contacts/${encodeURIComponent(contactId)}`))
);

// Same shape as GetContactData's bot_sessions_by_pk.
export async function fetchSessionContactRest(botId: string, sessionId: string): Promise<Row> {
  const data = await getData(botPath(botId, `/sessions/${encodeURIComponent(sessionId)}/contact`));
  return { ...data, messages: data.ad_id ? [{ ad_id: data.ad_id }] : [] };
}

export type ContactMatch = Row & { in_scope: boolean; wa_session_count?: number | null };

// Live contacts only. in_scope=false: the match belongs to bots outside the caller's access.
export async function lookupContactsRest(
  orgId: string,
  q: { phone?: string | null; email?: string | null; excludeId?: string; botId?: string },
): Promise<{ by_phone: ContactMatch | null; by_email: ContactMatch | null }> {
  const params = new URLSearchParams();
  if (q.phone) params.set('phone', q.phone);
  if (q.email) params.set('email', q.email);
  if (q.excludeId) params.set('exclude_id', q.excludeId);
  if (q.botId) params.set('bot_id', q.botId);
  return getData(`organizations/${encodeURIComponent(orgId)}/contacts/lookup`, params);
}

export async function fetchContactTagsRest(
  orgId: string,
  botId?: string | null,
): Promise<{ tag: string; contacts_count: number }[]> {
  const params = botId ? new URLSearchParams({ bot_id: botId }) : undefined;
  return (await getData(`organizations/${encodeURIComponent(orgId)}/contact-tags`, params)).items;
}
